/**
 * HierarchyContext — tenant-wide work-item hierarchy mode
 *
 * Three modes (admin-configurable in /admin/org-settings):
 *  - FULL          → Epic → Feature → Story → Task / Bug   (large enterprise)
 *  - LEAN          → Epic → Story → Task / Bug             (no Feature layer)
 *  - FEATURE_FIRST → Feature → Story → Task / Bug          (default — no Epics)
 *
 * UI hides disallowed types in pickers; the API enforces allowed types and
 * valid parent-child links. Existing data is never mutated when switching modes.
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useGetCurrentTenantQuery } from '@/store/api/apiSlice';
import { useAppSelector } from '@/store';
import { setTenantInfo, type Tenant } from '@/utils/tenant';
import { WorkItemType } from '@/types';
import {
  DEFAULT_HIERARCHY_MODE,
  getHierarchyDescriptor,
  HIERARCHY_OPTIONS,
  readHierarchyModeFromSettings,
  type HierarchyDescriptor,
  type HierarchyMode,
} from '@/utils/hierarchyMode';

export type { HierarchyDescriptor, HierarchyMode };
export {
  DEFAULT_HIERARCHY_MODE,
  getHierarchyDescriptor,
  HIERARCHY_OPTIONS,
  isAllowedForHierarchyMode,
} from '@/utils/hierarchyMode';

interface HierarchyContextValue {
  mode: HierarchyMode;
  descriptor: HierarchyDescriptor;
  isAllowed: (type: WorkItemType | string) => boolean;
  /** Suggested parent type for a given child (e.g. Story → Feature/Epic). null = top-level. */
  parentTypeFor: (type: WorkItemType | string) => WorkItemType | null;
  /** Allowed direct child types for a given parent (respects current mode). Empty = no children allowed. */
  childTypesFor: (parentType: WorkItemType | string) => WorkItemType[];
  /** Loading the tenant config (use to defer dependent UI). */
  loading: boolean;
}

const HierarchyContext = createContext<HierarchyContextValue | undefined>(undefined);

function readModeFromTenant(tenant: any): HierarchyMode {
  return readHierarchyModeFromSettings(tenant?.settings);
}

export function HierarchyProvider({ children }: { children: ReactNode }) {
  // Only fetch the current tenant once we actually have an access token.
  // Otherwise unauthenticated routes (login, signup) would fire a 401 on mount.
  const isAuthenticated = useAppSelector((s) => !!s.auth.accessToken);
  const { data: tenant, isLoading } = useGetCurrentTenantQuery(undefined, {
    refetchOnMountOrArgChange: false,
    skip: !isAuthenticated,
  });

  // Keep sessionStorage tenant slug in sync with the canonical server-side tenant.
  // Without this, a stale slug in sessionStorage gets sent as X-Tenant-Slug and
  // can collide with the JWT-resolved tenant (causing TENANT_HEADER_MISMATCH).
  useEffect(() => {
    if (tenant?.slug) {
      try { setTenantInfo(tenant as Tenant); } catch { /* ignore */ }
    }
  }, [tenant?.id, tenant?.slug]);

  const value = useMemo<HierarchyContextValue>(() => {
    const mode = readModeFromTenant(tenant);
    const descriptor = getHierarchyDescriptor(mode);
    const allowedSet = new Set<string>(descriptor.allowedTypes);

    const isAllowed = (type: WorkItemType | string) => allowedSet.has(String(type));

    const parentTypeFor = (type: WorkItemType | string): WorkItemType | null => {
      if (!allowedSet.has(String(type))) return null;
      const idx = descriptor.chain.indexOf(type as WorkItemType);
      // Leaf (Task/Bug) → parent is the deepest chain entry (Story when present).
      if (idx === -1) {
        if (type === WorkItemType.TASK || type === WorkItemType.BUG) {
          return descriptor.chain[descriptor.chain.length - 1] ?? null;
        }
        return null;
      }
      // Top of chain → no parent.
      if (idx === 0) return null;
      return descriptor.chain[idx - 1];
    };

    // Allowed direct child types for a given parent under the current hierarchy mode.
    //  - Chain entry that is NOT the deepest → next entry in chain.
    //  - Deepest chain entry (Story under FULL/LEAN/FEATURE_FIRST) → leaves [Task, Bug].
    //  - Leaf (Task/Bug) → no children (sub-tasks not modelled).
    //  - Type not in current chain (e.g. Epic when mode is FEATURE_FIRST) → none.
    const childTypesFor = (parentType: WorkItemType | string): WorkItemType[] => {
      const t = String(parentType) as WorkItemType;
      const idx = descriptor.chain.indexOf(t);
      if (idx === -1) return [];
      if (idx < descriptor.chain.length - 1) return [descriptor.chain[idx + 1]];
      // Deepest chain entry → leaves
      return [WorkItemType.TASK, WorkItemType.BUG];
    };

    return { mode, descriptor, isAllowed, parentTypeFor, childTypesFor, loading: isLoading };
  }, [tenant?.id, tenant?.settings?.hierarchyMode, isLoading]);

  return <HierarchyContext.Provider value={value}>{children}</HierarchyContext.Provider>;
}

export function useHierarchy(): HierarchyContextValue {
  const ctx = useContext(HierarchyContext);
  if (!ctx) {
    // Safe fallback so consumers never crash if rendered outside the provider
    // (e.g. in isolated tests). Returns the default mode.
    const descriptor = getHierarchyDescriptor(DEFAULT_HIERARCHY_MODE);
    const allowedSet = new Set<string>(descriptor.allowedTypes);
    return {
      mode: DEFAULT_HIERARCHY_MODE,
      descriptor,
      isAllowed: (type) => allowedSet.has(String(type)),
      parentTypeFor: (type) => {
        if (!allowedSet.has(String(type))) return null;
        const idx = descriptor.chain.indexOf(type as WorkItemType);
        if (idx === -1) {
          if (type === WorkItemType.TASK || type === WorkItemType.BUG) {
            return descriptor.chain[descriptor.chain.length - 1] ?? null;
          }
          return null;
        }
        if (idx === 0) return null;
        return descriptor.chain[idx - 1];
      },
      childTypesFor: (parentType) => {
        const t = String(parentType) as WorkItemType;
        const idx = descriptor.chain.indexOf(t);
        if (idx === -1) return [];
        if (idx < descriptor.chain.length - 1) return [descriptor.chain[idx + 1]];
        return [WorkItemType.TASK, WorkItemType.BUG];
      },
      loading: false,
    };
  }
  return ctx;
}
